// Commentary lives in the `commentary` text column. New rows store the
// structured `{read, pattern, useful}` shape as JSON; older rows (generated
// before the structured prompt shipped) are a single plain-text paragraph.
// This helper hides that mess from callers.

import { scrubGeneratedCopy } from "./scrubGeneratedCopy";

export interface StructuredCommentary {
  read: string;
  pattern: string | null;
  useful: string | null;
}

export function parseCommentary(raw: string | null | undefined): StructuredCommentary | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Looks like JSON?
  if (trimmed.startsWith("{")) {
    try {
      const obj = JSON.parse(trimmed) as Partial<StructuredCommentary>;
      if (typeof obj.read === "string" && obj.read.trim()) {
        return {
          read: scrubGeneratedCopy(obj.read.trim()),
          pattern: typeof obj.pattern === "string" && obj.pattern.trim() ? scrubGeneratedCopy(obj.pattern.trim()) : null,
          useful: typeof obj.useful === "string" && obj.useful.trim() ? scrubGeneratedCopy(obj.useful.trim()) : null,
        };
      }
    } catch {
      // Fall through to plain-text treatment.
    }
  }
  return { read: scrubGeneratedCopy(trimmed), pattern: null, useful: null };
}

// Tagline / tier metadata for a Havoc Score. Big text on the Person page
// hero plus tier coloring. Phrasing is brand-voice (mocking but not cruel).
export interface HavocTier {
  band: 0 | 1 | 2 | 3 | 4;
  label: string;          // Short tier name (sidebar uses this)
  tagline: string;        // The hero line under the giant number
  textHi: string;         // CSS variable for the bright accent text color
  textSoft: string;       // Softer fill for backgrounds
  borderHi: string;
  ring: string;           // Tailwind ring color
}

const TIER_PRESETS: Record<HavocTier["band"], Omit<HavocTier, "tagline">> = {
  0: { band: 0, label: "Squeaky Clean",  textHi: "var(--color-sage-hi)",   textSoft: "var(--color-sage-soft)",   borderHi: "var(--color-sage-hi)",   ring: "ring-[color:var(--color-sage-hi)]/30" },
  1: { band: 1, label: "Nuisance",       textHi: "var(--color-blue-hi)",   textSoft: "var(--color-blue-soft)",   borderHi: "var(--color-blue-hi)",   ring: "ring-[color:var(--color-blue-hi)]/30" },
  2: { band: 2, label: "Colorful",       textHi: "var(--color-gold-hi)",   textSoft: "var(--color-gold-soft)",   borderHi: "var(--color-gold-hi)",   ring: "ring-[color:var(--color-gold-hi)]/30" },
  3: { band: 3, label: "Chaos Magnet",   textHi: "var(--color-orange-hi)", textSoft: "var(--color-orange-soft)", borderHi: "var(--color-orange-hi)", ring: "ring-[color:var(--color-orange-hi)]/30" },
  4: { band: 4, label: "Cataclysm",      textHi: "var(--color-rose-hi)",   textSoft: "var(--color-rose-soft)",   borderHi: "var(--color-rose-hi)",   ring: "ring-[color:var(--color-rose-hi)]/30" },
};

// Mocking-but-not-cruel taglines. We rotate within a band based on the
// score so two people with similar scores don't get identical headlines.
const TAGLINES: Record<HavocTier["band"], string[]> = {
  0: [
    "Spotless. Almost suspiciously so.",
    "Zero hits. The ledger is blank.",
    "Citizen of the year, statistically speaking.",
    "Either innocent or unusually careful.",
  ],
  1: [
    "Some traffic, no teeth.",
    "Forgot a turn signal a few times.",
    "Allergic to seat belts, mostly harmless.",
    "Annoying, not alarming.",
  ],
  2: [
    "Made some choices.",
    "A real connoisseur of poor decisions.",
    "Local fixture at a couple of municipal courts.",
    "Walks a colorful line.",
  ],
  3: [
    "On a first-name basis with the docket.",
    "Getting their cardio in at municipal court.",
    "The court system's preferred customer.",
    "Operates at a tempo that's really impressive.",
  ],
  4: [
    "If chaos paid commission, this would be six figures.",
    "An entire career, mostly in courthouses.",
    "Owns a wing of the county docket.",
    "The full docket menu, ordered twice.",
  ],
};

function bandFor(score: number): HavocTier["band"] {
  if (score <= 15) return 0;
  if (score <= 35) return 1;
  if (score <= 55) return 2;
  if (score <= 75) return 3;
  return 4;
}

/** A single, plain-English explanation of the Havoc Score that any UI
 *  surface can drop into a `title` attribute, tooltip, or info-popover.
 *  Centralizing it here keeps the score's vocabulary consistent across
 *  the records list, person hero, command palette, story page, etc. */
export const HAVOC_SCORE_EXPLAINER =
  "Havoc Score (0–100): a heuristic from offense count + severity. " +
  "Tiers: 0–15 Squeaky Clean · 16–35 Nuisance · 36–55 Colorful · " +
  "56–75 Chaos Magnet · 76–100 Cataclysm. Internal triage signal - " +
  "not a legal assessment.";

export function havocTier(score: number | null | undefined): HavocTier | null {
  if (score === null || score === undefined) return null;
  const band = bandFor(score);
  const preset = TIER_PRESETS[band];
  const lines = TAGLINES[band];
  const tagline = lines[score % lines.length] ?? lines[0]!;
  return { ...preset, tagline };
}
